import { Component, ChangeDetectionStrategy, inject, signal, DestroyRef, computed, input, CUSTOM_ELEMENTS_SCHEMA, DOCUMENT } from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { FinalWidgetPremiumPromoWeb, humanize, IElement, IOfferCard, UrlPreviewSeo } from '../../types';
import { InViewportModule } from 'ng-in-viewport';
import { MetaService } from '../../meta.service';
import { HttpClient } from '@angular/common/http';
import { backend } from '../../../environments/environment';
import { TranslateModule } from '@ngx-translate/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { LocationService } from '../../location.service';
import { BucketPipe } from "../../bucket.pipe";
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { PaintDirective } from '../../paint.directive';
import { DateTime } from 'luxon';
import { CurrencyPipe, NgTemplateOutlet } from '@angular/common';
import { ImageFallbackDirective } from '../../image-fallback.directive';
import { MatDividerModule } from '@angular/material/divider';
import { catchError, combineLatest, debounceTime, distinctUntilChanged, filter, from, map, merge, of, switchMap, take, tap } from 'rxjs';
import { AuthService } from '../../auth.service';
import { Analytics, logEvent } from '@angular/fire/analytics';
import { OfferItemComponent } from "../../offers/offer-item/offer-item.component";

@Component({
  selector: 'get-premium',
  templateUrl: './get-premium.component.html',
  styleUrls: ['./get-premium.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [InViewportModule, TranslateModule, BucketPipe, RouterLink, MatIconModule, MatButtonModule, PaintDirective, NgTemplateOutlet, ImageFallbackDirective, CurrencyPipe, MatDividerModule, OfferItemComponent],
  host: {
    '[style.--promo-bg-color]': 'view().background_color',
    '[style.--promo-text-color]': 'view().text_color',
    '[style.--promo-button-bg-color]': 'view().button_background_color',
    '[style.--promo-button-text-color]': 'view().button_text_color',
    '[class.translatable]': 'true'
  },
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export default class GetPremiumComponent {
  activated = inject(ActivatedRoute);
  meta = inject(MetaService);
  http = inject(HttpClient);
  destroy = inject(DestroyRef);
  router = inject(LocationService);
  auth = inject(AuthService);
  ga = inject(Analytics);
  document = inject(DOCUMENT);
  
  promo = input<string|undefined>(undefined);
  
  loading = signal<boolean>(false);
  rendered = signal<boolean>(false);

  is_retail = signal<boolean>(false);

  view = signal<FinalWidgetPremiumPromoWeb>({
    background_color: 'white',
    text_color: '#181921',
    button_background_color: '#ff4735',
    button_text_color: 'white',
    base_price: '49.99',
    banner_desktop: '',
    banner_desktop_maltese: '',
    banner_mobile: '',
    banner_mobile_maltese: '',
    duration: 365,
    unit: 'year',
  });//TODO: make some defaults

  checkout_url = computed(() => this.is_retail() ? ['/purchase', 'gift'] : ['/checkout', 'yearly']);

  checkout_query_params = computed(() => {
    const view = this.view();
    return view?.code ? {
      code: view.code
    } : {}
  });

  base_price = computed(() => {
    const base_price = this.view().base_price;
    return isNaN(Number(base_price)) ? 49.99 : Number(Number(base_price).toFixed(2));
  });

  discounted_price = computed(() => {
    const discounted_price = this.view().discounted_price;
    return !discounted_price || isNaN(Number(discounted_price)) ? null : Number(Number(discounted_price).toFixed(2));
  });

  final_price = computed(() => {
    let discounted_price = this.discounted_price();
    if(discounted_price === null){
      return this.base_price();
    }

    return discounted_price;
  });

  price_per_day = computed(() => {
    const view = this.view();
    const final_price = this.final_price();
    if (view) {
      return (final_price / view.duration).toFixed(2);
    }
    return null;
  });

  discounted = computed(() => {
    const base_price = this.base_price();
    const final_price = this.final_price();
    return base_price > final_price;
  });

  reviews = [
    {
      id: '1',
      review: "Cloudigo Premium is an investment!",
      premium: true,
      reviewer: 'K. Aquilina',
      rating: 5,
      ratings: [1, 1, 1, 1, 1]
    },
    {
      id: '2',
      review: "Cloudigo helps with the rising costs that we are facing at this time. I recommend it to anyone who likes to shop and save money",
      premium: true,
      reviewer: 'N. Saliba',
      rating: 5,
      ratings: [1, 1, 1, 1, 1]
    },
    {
      id: '3',
      review: "I have saved around 350 euros in discounts",
      premium: true,
      reviewer: 'P. Cipriott',
      rating: 5,
      ratings: [1, 1, 1, 1, 1]
    },
    {
      id: '4',
      review: "This is a great app to have, the discounts are real with no fishy terms and conditions. Very worth it.",
      premium: true,
      reviewer: 'L. Mallia',
      rating: 5,
      ratings: [1, 1, 1, 1, 1]
    },
    {
      id: '5',
      review: "Get Cloudigo Premium, you get back more than you pay for it. So very worth it.",
      premium: true,
      reviewer: 'M. Formosa',
      rating: 5,
      ratings: [1, 1, 1, 1, 1]
    },
    {
      id: '6',
      review: "No hidden charges, easy to use and real discounts app, worth the premium price.",
      premium: true,
      reviewer: 'C. Grima',
      rating: 5,
      ratings: [1, 1, 1, 1, 1]
    },
    {
      id: '7',
      review: "The savings I had in the past year was a great help with the rise of cost of living. Discounts are just great",
      premium: true,
      reviewer: 'M. Fenech',
      rating: 5,
      ratings: [1, 1, 1, 1, 1]
    },
    {
      id: '8',
      review: "I try to use it regularly since I save a lot of money with it for example in 6 months I already saved around 120 euros. Wowww that's a lot. Thank you Cloudigo",
      premium: true,
      reviewer: 'E. Deguara',
      rating: 5,
      ratings: [1, 1, 1, 1, 1]
    },
  ]

  carousel_configs: { filter: string; sort: string; days?: number }[] = [
    {
      filter: ['5b3e190bbbe26a4ac441997f'].join(','),
      sort: 'value',
      days: 512,
    },
    {
      filter: ['5b3e193de7179a1ccf3cbe80'].join(','),
      sort: 'value',
    },
    {
      filter: ['5b6ae844fb6fc00289f224c3', '60214ecf8b26a325fc4feac4', '5bfe8130fb6fc006ff26e38d'].join(','),
      sort: 'value'
    },
    {
      filter: ['61adf12f3d221d1c5e25c8de', '623b01f14b7d46791b69d9f1', '5bfe83bafb6fc006ff26e4dc', '61093797e5421627faec58a8', '612e3dee4717082231513c10'].join(','),
      sort: 'value'
    },
    {
      filter: ['5b3e1966e7179a1ccf3cbe88', '5dbbee0b82b3b08dba0e7782'].join(','),
      sort: 'value'
    }
  ];

  offers = signal<{ [key: number]: IOfferCard[] }>({
    1: [],
    2: [],
    3: [],
    4: [],
    5: []
  })

  offers_list = computed(() => Object.values(this.offers()));

  video_url = 'https://www.youtube.com/embed/Qzh9RvYPnVw?autoplay=1&mute=1&rel=0';

  constructor() {
    const now = DateTime.now();

    combineLatest([this.activated.data.pipe(map(data => !!data.is_retail || false), distinctUntilChanged()), toObservable(this.promo)]).pipe( 
      tap(([is_retail, promo]) => {
        this.is_retail.set(!!is_retail);
        this.loading.set(true);
      }),
      debounceTime(100),
      takeUntilDestroyed(this.destroy),
      switchMap(([is_retail, promo]) => this.http.get<IElement<FinalWidgetPremiumPromoWeb>>(`${backend}element?identifier=${is_retail ? 'gift' : 'yearly'}` + (promo ? `&reference=${promo}` : '')))
    ).subscribe({
      next: element => {
        element.variables.code = element.variables.code?.replace('{{MONTH}}', now.toFormat('MMMM')).replace('{{YEAR}}', now.toFormat('yy')).replace(/{{DATE(:([SusmhHZzadEMyGkWinoqDtTfFx]+)|)}}/gm, (_,__,format) => now.toFormat(format||'MMMMyy')).toUpperCase() ?? '';
        this.view.update((view) => {
          return Object.assign(Object.assign({}, view), element.variables);
        });

        const meta: Partial<UrlPreviewSeo> = {
          title: 'cloudigo Malta - Premium Plan', 
          description: 'Access 1000+ offers from 350+ brands in Malta and Gozo up to 50% OFF all year round. Upgrade now.', 
          image: '//images/meta/premium.png'
        };

        if(this.is_retail()){
          meta.title = 'cloudigo Malta - Buy Premium as a Gift';
          meta.description = 'Give the gift of discounts. 12 months of 1000+ discounts from 350+ brands in Malta and Gozo for the ones you love.';
        }

        if(element.variables.url_preview){
          Object.assign(meta, element.variables.url_preview);
        }

        this.meta.setTags(meta.title, meta.description, meta.image);
        this.loading.set(false);
      },
      error: error => {
        console.warn('woops', error);
        this.loading.set(false);
      }
    });

    this.carousel_configs.forEach((config, index) => {
      this.http.get<IOfferCard[]>(backend + 'v3/offers/featured', {
        params: {
          ...config,
          limit: 40,
          plans: 'premium'
        }
      }).subscribe(offers => {
        this.offers.update((list) => ({ ...list, [index + 1]: offers }));
      });
    });

    if(this.meta.isBrowser){
      merge(from(this.auth.fireAuth.authStateReady()),toObservable(this.is_retail)).pipe(
        debounceTime(500),
        filter(() => !!this.auth.fireAuth.currentUser),
        switchMap(() => this.http.post<{ message?: string }>(backend + 'browse', {
          page: this.is_retail() ? 'gift' : 'yearly', theme: 'default', campaign: this.router.initialURL?.searchParams.get('utm_campaign') || 'web_direct'
        })),
        takeUntilDestroyed(this.destroy),
        catchError(e => of()),
        take(1)
      ).subscribe(() => { 
        logEvent(this.ga, 'view_item', {
          currency: 'EUR',
          value: this.final_price(),
          items: [
            {
              item_id: this.is_retail() ? 'gift' : 'yearly',
              item_name: humanize(this.is_retail() ? 'gift' : 'yearly')!,
              price: this.final_price(),
              ...this.view().code ? {
                coupon: this.view().code,
                discount: this.base_price() - this.final_price()
              } : {},
              quantity: 1
            }
          ]
        });
      });
  
    }

  }

  onRender(event: { target: Element, visible: boolean }) {
    // console.log(event);
    if (event && event.visible && !this.rendered()) {
      this.rendered.set(true);
    }
  }
}
